import {create} from 'zustand';
import {
  bulkDownload,
  createNewProject,
  deleteOneProject, downloadTranslation,
  fetchProjects,
  getProjectStatus, resetFeature,
  sendFeedback
} from "../services/projectService.ts";
import useUiStore from "./useUiStore.ts";
import {IProject} from '../models/interfaces/project.interface.ts';
import {INewProject} from "../models/interfaces/newProject.interface.ts";
import {IFeedbackForm} from "../models/interfaces/feedbackForm.interface.ts";
import {IProjectStatus} from "../models/interfaces/projectStatus.interface.ts";
import {transformProjectsDtoToProjects} from "../utils/transformers/project.transformer.ts";
import {IProjectDto} from "../models/interfaces/dtos/project.dto.interface.ts";
import {IItemProgress} from "../models/interfaces/itemProgress.interface.ts";


interface ProjectStore {
  projects: IProject[];
  projectsToView: string[];
  candidates: INewProject[];
  getProjects: () => Promise<void>;
  createProject: (newProject: INewProject) => Promise<void>;
  getProjectStatus: (projectId: string) => Promise<void>;
  sendProjectFeedback: (projectId: string, targetLang: string, feedbackForm: IFeedbackForm) => Promise<void>
  deleteProject: (projectId: string) => Promise<void>;
  downloadProject: (projectId: string, projectName: string, isTranscribeOnly: boolean) => Promise<void>;
  bulkDownload: (projectIds: string[]) => Promise<void>;
  resetFeature: (projectId: string, feature: string, languageCode?: string) => Promise<void>;
  filterProjectsByTerm: (term: string) => void;
  filterProjectByDownloadFlag: () => void;
  clearProjectsToView: () => void;
}


export const useProjectsStore = create<ProjectStore>((set, get) => ({
  projects: [] as IProject[],
  projectsToView: [] as string[],
  candidates: [] as INewProject[],
  getProjects: async (): Promise<void> => {
    try {
      useUiStore.getState().showLoader();
      const projectsDto: IProjectDto[] = await fetchProjects()
      const projects: IProject[] = transformProjectsDtoToProjects(projectsDto);
      set(() => ({projects}))
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      useUiStore.getState().hideLoader();
    }
  },
  createProject: async (newProject: INewProject): Promise<void> => {
    try {
      const projectDto: IProjectDto = await createNewProject(newProject)
      const project: IProject = transformProjectsDtoToProjects([projectDto])[0];
      set((state: ProjectStore) => ({
        projects: [...state.projects, project],
      }));
    } catch (error) {
      console.error('Error creating project:', error);
    }
  },
  getProjectStatus: async (projectId: string): Promise<void> => {
    try {
      const response: IProjectStatus = await getProjectStatus(projectId);

      set((state) => ({
        projects: state.projects.map((project) =>
          project.id === response._id
            ? {...project, transcription: response.transcription, translations: response.translations}
            : project
        ),
      }));
    } catch (error) {
      console.error("Error getting project status:", error);
    }
  },
  sendProjectFeedback: async (projectId: string, targetLang: string, feedbackForm: IFeedbackForm): Promise<void> => {
    try {
      await sendFeedback(projectId, targetLang, feedbackForm)
      console.log("Feedback sent successfully");
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  },
  deleteProject: async (projectId: string): Promise<void> => {
    try {
      await deleteOneProject(projectId);
      set((state) => ({
        projects: state.projects.filter((project) => project.id !== projectId),
      }));
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  },
  downloadProject: async (projectId: string, projectName: string, isTranscribeOnly: boolean): Promise<void> => {
    try {
      const filename = `${isTranscribeOnly ? 'transcription' : 'translation'}_${projectName ?? projectId}`;
      await downloadTranslation(projectId, filename)
      //   markProjectAsDownloaded
      set((state) => ({
        projects: state.projects.map((project) =>
          project.id === projectId
            ? {...project, downloaded: true}
            : project
        ),
      }));
    } catch (e) {
      console.error("Error downloading project:", e);
    }
  },
  bulkDownload: async (projectIds: string[]): Promise<void> => {
    try {
      await bulkDownload(projectIds)
      set((state) => ({
        projects: state.projects.map((project) =>
          projectIds.includes(project.id)
            ? {...project, downloaded: true}
            : project
        ),
      }))
    } catch (e) {
      console.error("Error bulk downloading projects:", e);
    }
  },
  resetFeature: async (projectId: string, feature: string, languageCode?: string): Promise<void> => {
    try {
      const projectDto: IProjectDto = await resetFeature(projectId, feature, languageCode);
      const project: IProject = transformProjectsDtoToProjects([projectDto])[0];
      set((state: ProjectStore) => ({
        projects: state.projects.map((p: IProject) =>
          p.id === project.id ? project : p
        ),
      }));
    } catch (error) {
      console.error("Error resetting feature:", error);
      throw error;
    }
  },
  filterProjectsByTerm: (term: string): void => {
    set((state: ProjectStore) => ({
      projectsToView: state.projects.filter((project: IProject) =>
        project.name.toLowerCase().includes(term.toLowerCase())
      ).map((project: IProject) => project.id)
    }));
  },
  filterProjectByDownloadFlag: (): void => {
    const isCompleted = (p: IProject) => {
      const st = p.featuresStates;

      const subsDownloaded = st?.subtitleTranslations
        ? Object.values(st.subtitleTranslations).some(v => v?.downloaded)
        : false;

      const audioDownloaded = st?.audioDescriptions
        ? Object.values(st.audioDescriptions).some(v => v?.downloaded)
        : false;

      const metaDownloaded = st?.extendedMetadata?.downloaded ?? false;

      const dialogueDownloaded = st?.dialogueList?.downloaded ?? false;

      // ✅ completed if ANY of them is downloaded
      return subsDownloaded || audioDownloaded || metaDownloaded || dialogueDownloaded;
    };

    set((state: ProjectStore) => ({
      // keep only NOT-completed projects in view
      projectsToView: state.projects
        .filter(p => !isCompleted(p))
        .map(p => p.id),
    }));
  },
  clearProjectsToView: () => set(() => ({
    projectsToView: []
  })),
}));